import { QuestionCircleOutlined } from '@ant-design/icons';
import {
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Switch,
} from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { RevxButton } from 'components/RevxButton';
import { RevxSpin } from 'components/RevxSpin';
import { ElementTypeSelectOptions, PartialMarkup } from 'domains/markupDomain';
import { merge } from 'lodash-es';
import moment, { Moment } from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DRAWER_WIDTH } from 'utils/constants';
import { RULE_INTEGER, RULE_TEXT_LEN_255 } from 'utils/validators';

const { Option } = Select;

const ElementOverrideItemForm = () => {
  const { t } = useTranslation();
  const formItemLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 16 },
  };
  const formActionsLayout = {
    labelCol: { span: 7 },
    wrapperCol: { span: 16, offset: 7 },
  };

  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const saving = useAppSelector(
    (state) => state.loading.effects.markup.saveMarkup
  );
  const item = useAppSelector((state) => state.markup.curElementOverrideMarkup);

  const onClose = () => {
    dispatch.markup.setState({ curElementOverrideMarkup: undefined });
  };
  const onFinish = async (values: any) => {
    let updated: PartialMarkup = merge({}, item, values);
    updated.markupRanges![0].markupPct = values.markupPct;
    updated.startDt = (values.dateRange[0] as Moment).format('YYYY-MM-DD');
    updated.endDt = (values.dateRange[1] as Moment).format('YYYY-MM-DD');
    await dispatch.markup.saveMarkup(updated);
    onClose();
  };

  const onRemove = async () => {
    if (item?.id) {
      await dispatch.markup.removeMarkup(item);
      onClose();
    }
  };

  const disabledDate: (current: Moment) => boolean = (current) => {
    // Can not select days before today
    return current < moment().startOf('day');
  };

  const actionBar = (
    <Form.Item {...formActionsLayout}>
      <Space>
        <RevxButton type="primary" htmlType="submit" brandPurple>
          {t('common.save')}
        </RevxButton>
        <RevxButton onClick={onClose}>{t('common.cancel')}</RevxButton>
        {item?.id && (
          <>
            <Popconfirm
              title={t('common.confirmQuestion')}
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={onRemove}
              okText={t('common.ok')}
              cancelText={t('common.cancel')}
            >
              <RevxButton danger>{t('common.remove')}</RevxButton>
            </Popconfirm>
          </>
        )}
      </Space>
    </Form.Item>
  );

  return (
    <Drawer
      destroyOnClose
      title={
        item?.id
          ? t('platform.markup.titles.edit')
          : t('platform.markup.titles.create')
      }
      placement="right"
      width={DEFAULT_DRAWER_WIDTH}
      closable={true}
      onClose={onClose}
      visible={Boolean(item)}
    >
      <RevxSpin spinning={saving}>
        <Form
          {...formItemLayout}
          layout={'horizontal'}
          form={form}
          initialValues={{
            ...item,
            dateRange: [moment(item?.startDt), moment(item?.endDt)],
          }}
          onFinish={onFinish}
        >
          <Form.Item
            label={t('common.active')}
            name="active"
            valuePropName="checked"
          >
            <Switch disabled={!Boolean(item?.id)} />
          </Form.Item>

          <Form.Item
            label={t('platform.markup.labels.jobElement')}
            name={'elementType'}
            rules={[{ required: true }]}
          >
            <Select
              disabled={Boolean(item?.id)}
              getPopupContainer={(e) => {
                return e as HTMLElement;
              }}
            >
              {ElementTypeSelectOptions.map((t) => (
                <Option key={t.code} value={t.code}>
                  {t.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('common.description')}
            name="description"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input.TextArea rows={3} />
          </Form.Item>

          <Form.Item
            label={t('platform.markup.labels.markup')}
            name="markupPct"
            rules={[RULE_INTEGER, { required: true }]}
          >
            <Input prefix="%" />
          </Form.Item>

          <Form.Item
            label={t('platform.markup.labels.dateRange')}
            name="dateRange"
            rules={[{ required: true }]}
          >
            <DatePicker.RangePicker disabledDate={disabledDate} />
          </Form.Item>

          {actionBar}
        </Form>
      </RevxSpin>
    </Drawer>
  );
};
export default ElementOverrideItemForm;
